import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Drawer, Box, Typography, IconButton, List, ListItem,
  Divider, Tooltip, Alert, Chip, Button,
} from '@mui/material';
import { Close, DeleteOutline, Favorite, WhatsApp } from '@mui/icons-material';
import { useAuth } from './AuthContext';

const THEME_CHIP_STYLES = {
  islamic:  { bg: '#e8f5e9', color: '#2e7d32' },
  javanese: { bg: '#fff3e0', color: '#e65100' },
  arabic:   { bg: '#e3f2fd', color: '#1565c0' },
  christian:{ bg: '#f3e5f5', color: '#6a1b9a' },
  sanskrit: { bg: '#ffebee', color: '#c62828' },
  chinese:  { bg: '#fbe9e7', color: '#d84315' },
  modern:   { bg: '#e0f2f1', color: '#00695c' },
  japanese: { bg: '#fce4ec', color: '#ad1457' },
};

const MEANING_CLAMP_THRESHOLD = 80;

export default function FavoritesDrawer({ open, onClose }) {
  const { t } = useTranslation();
  const { favorites, favoritesLimit, atFavoritesLimit, removeFavorite } = useAuth();
  const [expanded, setExpanded] = useState(new Set());

  const toggleExpand = (name) => {
    setExpanded(prev => {
      const next = new Set(prev);
      next.has(name) ? next.delete(name) : next.add(name);
      return next;
    });
  };

  const handleShareWhatsApp = () => {
    const list = favorites.map(f => `• *${f.name}* — ${f.meaning}`).join('\n');
    const text = encodeURIComponent(
      `Nama bayi favorit saya dari CumaloApp:\n\n${list}\n\nCari nama bayi di:\nhttps://cumalochannel.com/nama-bayi/`
    );
    window.open(`https://wa.me/?text=${text}`, '_blank', 'noopener,noreferrer');
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100%', sm: 380 }, display: 'flex', flexDirection: 'column' } }}
    >
      {/* Header */}
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Favorite sx={{ color: 'error.main', fontSize: 20 }} />
          <Typography variant="h6">{t('favoritesDrawerTitle')}</Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <Close />
        </IconButton>
      </Box>

      {/* Count */}
      <Box sx={{ px: 2, py: 1 }}>
        <Typography variant="caption" color="text.secondary">
          {t('favoritesCount', { count: favorites.length, limit: favoritesLimit })}
        </Typography>
      </Box>

      {atFavoritesLimit && (
        <Box sx={{ px: 2, pb: 1 }}>
          <Alert severity="warning" sx={{ fontSize: '0.75rem', py: 0.5 }}>
            {t('favoritesLimitBanner', { limit: favoritesLimit })}
          </Alert>
        </Box>
      )}

      <Divider />

      {/* List */}
      {favorites.length === 0 ? (
        <Box sx={{ p: 4, textAlign: 'center', flex: 1 }}>
          <Favorite sx={{ fontSize: 40, color: 'error.light', mb: 1, opacity: 0.35 }} />
          <Typography color="text.secondary" variant="body2">
            {t('favoritesEmpty')}
          </Typography>
        </Box>
      ) : (
        <List dense sx={{ flex: 1, overflow: 'auto', py: 0 }}>
          {favorites.map((fav, idx) => {
            const isExpanded = expanded.has(fav.name);
            const isLong = (fav.meaning || '').length > MEANING_CLAMP_THRESHOLD;
            const themeStyle = fav.theme ? THEME_CHIP_STYLES[fav.theme] : null;
            const themeKey = fav.theme
              ? `theme${fav.theme.charAt(0).toUpperCase() + fav.theme.slice(1)}`
              : null;

            return (
              <React.Fragment key={fav.id ?? fav.name}>
                <ListItem alignItems="flex-start" sx={{ gap: 1, py: 1.5 }}>
                  {/* Index badge */}
                  <Box sx={{
                    minWidth: 22, height: 22, borderRadius: '50%',
                    bgcolor: 'primary.main', color: 'white',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '0.68rem', fontWeight: 700, flexShrink: 0, mt: 0.4,
                  }}>
                    {idx + 1}
                  </Box>

                  {/* Content */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', mb: 0.25 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        {fav.name}
                      </Typography>
                      {themeStyle && themeKey && (
                        <Chip
                          label={t(themeKey)}
                          size="small"
                          sx={{
                            height: 18, fontSize: '0.65rem', fontWeight: 600,
                            bgcolor: themeStyle.bg, color: themeStyle.color,
                            border: 'none',
                          }}
                        />
                      )}
                    </Box>

                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={!isExpanded && isLong ? {
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      } : {}}
                    >
                      {fav.meaning}
                    </Typography>

                    {isLong && (
                      <Button
                        size="small"
                        sx={{ p: 0, minWidth: 0, fontSize: '0.72rem', textTransform: 'none', mt: 0.25 }}
                        onClick={() => toggleExpand(fav.name)}
                      >
                        {isExpanded ? t('showLessMeaning') : t('showMoreMeaning')}
                      </Button>
                    )}
                  </Box>

                  {/* Delete */}
                  <Tooltip title={t('removeFromFavoritesTooltip')}>
                    <IconButton
                      edge="end"
                      size="small"
                      onClick={() => removeFavorite(fav.name)}
                      sx={{ flexShrink: 0, alignSelf: 'flex-start', mt: 0.25 }}
                    >
                      <DeleteOutline fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </ListItem>
                <Divider component="li" />
              </React.Fragment>
            );
          })}
        </List>
      )}

      {/* Share footer */}
      {favorites.length > 0 && (
        <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button
            fullWidth
            variant="outlined"
            startIcon={<WhatsApp />}
            onClick={handleShareWhatsApp}
            sx={{
              color: '#25D366', borderColor: '#25D366', textTransform: 'none',
              '&:hover': { borderColor: '#1ea952', backgroundColor: 'rgba(37,211,102,0.04)' },
            }}
          >
            {t('shareFavoritesWhatsApp')}
          </Button>
        </Box>
      )}
    </Drawer>
  );
}
